<script setup lang="ts">
import { useCollectionContents } from '@ilteducation/content-collections';
import type { ContentCollection } from '@ilteducation/content-collections-types';
import { computed, ref, watch } from 'vue';
import VideoThumbnail from '@/components/videos/VideoThumbnail.vue';
import { useLocale } from '@/plugins/translation';
import { useCategories } from '@ilteducation/content-ref-data';
import { useVideos, Video } from '@ilteducation/videos';
import type { MaterialV1 } from '@ilteducation/content-registry-types';
import ArticlePreview from '@/components/ArticlePreview.vue';
import { useDialogFocus } from '@ilteducation/components';
import type { ContentWithDuplicates } from '@ilteducation/content';
import MaterialDialog from '@/components/material/MaterialDialog.vue';
import MaterialThumbnail from '@/components/material/MaterialThumbnail.vue';

const props = defineProps<{
  collection: ContentCollection;
}>();

const collectionId = computed(() => props.collection.id);
const { contents, isLoading } = useCollectionContents(collectionId, ref({ limit: 4 }));
// Hard limit to 4 due to useCollectionContents.contents computation do not acknowledge useCollectionContents.query, only on initial load
// content-collection-content pinia store can be rewritten by another use of useCollectionContents (CollectionContents.vue in this case)
const limitedContents = computed(() => contents.value.slice(0, 4));
const videoStore = useVideos();

const videosInCollection = computed(() => contents.value.filter((content) => content.type === 'VideoV1'));
const videos = ref<Video[]>([]);

watch(
  videosInCollection,
  async (newVideosInCollection) => {
    videos.value = await Promise.all(newVideosInCollection.map((vid) => videoStore.getVideoById(vid.id)));
  },
  { immediate: true },
);

const { categories } = useCategories();
const categoryInCurrentRealm = (categoryIds: string[]) =>
  categoryIds.find((categoryId) =>
    categories.value.find((category) => category.kind === 'subject' && category.id === categoryId),
  );

const videoById = (id: string): Video => videos.value.find((vid) => vid.id === id)!;
const { t, l } = useLocale();

const selectedMaterial = ref<ContentWithDuplicates>();
const previewDialog = ref(false);
const { rememberFocus } = useDialogFocus([previewDialog]);

const onMaterialClick = (material: MaterialV1) => {
  rememberFocus();
  selectedMaterial.value = material as ContentWithDuplicates;
  previewDialog.value = true;
};

const onChangeMaterial = (material: MaterialV1) => {
  selectedMaterial.value = material as ContentWithDuplicates;
};
</script>

<template>
  <v-row>
    <template v-for="content in limitedContents">
      <VideoThumbnail
        v-if="content.type === 'VideoV1' && videoById(content.id)"
        :key="content.id"
        :video="videoById(content.id)"
        :subject-id="categoryInCurrentRealm(content.categories) || ''"
        :use-theme-styling="false"
      />
      <v-col v-else-if="content.type === 'ArticleV1'" cols="6" sm="4" md="3" :key="content.id">
        <ArticlePreview :article="content" />
      </v-col>
      <v-col v-else-if="content.type === 'MaterialV1'" cols="6" sm="4" md="3" :key="content.id">
        <MaterialThumbnail :material="content" @click="onMaterialClick" />
      </v-col>
    </template>
    <div v-if="!isLoading && contents.length === 0">
      <div class="pa-2 pa-sm-3 pa-xl-4">{{ t(l.content_collections.no_content_in_collection) }}</div>
    </div>
  </v-row>
  <MaterialDialog
    v-if="selectedMaterial"
    :model-value="previewDialog"
    :selected-material="selectedMaterial as MaterialV1"
    @update:model-value="(val) => (previewDialog = val)"
    @update:selected-material="onChangeMaterial"
  />
</template>
